import { useEffect, useId, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  galleryLightboxClass,
  resolveGalleryLightbox,
  type GalleryLightbox,
} from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { LucideIcon } from '../../icons/LucideIcon';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';
import {
  galleryArrangementClass,
  readGalleryFrameSlots,
  resolveGalleryArrangement,
  type GalleryFrameSlot,
} from './galleryFrames';

const FRAME_WIDTHS = [480, 720, 960, 1280] as const;

function frameSrc(rawUrl: string | undefined, width: number): string | undefined {
  if (!rawUrl) return undefined;
  return buildSiteImageProxyUrl(rawUrl, width, 78);
}

function frameSrcSet(rawUrl: string | undefined): string | undefined {
  if (!rawUrl) return undefined;
  return buildSiteImageSrcSet(rawUrl, [...FRAME_WIDTHS], 78);
}

function padIndex(index: number): string {
  return String(index).padStart(2, '0');
}

function slotAlt(slot: GalleryFrameSlot, sectionTitle: string): string {
  return slot.caption || sectionTitle || `Photo ${slot.index}`;
}

export function ImageGallery({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const arrangement = resolveGalleryArrangement(block.registryBlockKey);
  const lightboxLook = resolveGalleryLightbox(block.registryBlockKey, block.content);
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const slots = readGalleryFrameSlots(block, locale, { includeEmpty: editable });
  const target = isActive ? activeEditTarget : null;
  const reactId = useId().replace(/:/g, '');
  const groupId = `gal-${block.blockId || reactId}`;
  const rootRef = useRef<HTMLElement>(null);
  const titleId = `${groupId}-title`;
  const closeLabel = pickChrome('galleryClose', locale);
  const prevLabel = pickChrome('galleryPrev', locale);
  const nextLabel = pickChrome('galleryNext', locale);
  const filmstripNavLabel = pickChrome('galleryFilmstripNav', locale);
  const goToPhotoLabel = pickChrome('galleryGoToPhoto', locale);
  const filled = slots.filter((slot) => slot.rawUrl);
  const filledKey = filled.map((slot) => `${slot.index}:${slot.rawUrl}`).join('|');

  useEffect(() => {
    if (editable) return;
    const root = rootRef.current;
    if (!root) return;

    function lightboxRoot(): HTMLElement | null {
      return root!.querySelector<HTMLElement>('.ws-gallery__lightbox');
    }

    function ownsLightboxNode(node: Element | null): boolean {
      if (!node) return false;
      if (root!.contains(node)) return true;
      const lightbox = node.closest?.('.ws-gallery__lightbox');
      return Boolean(lightbox && lightboxRoot() === lightbox);
    }

    function paintLightbox(hit: Element) {
      const lightbox = lightboxRoot();
      const img = lightbox?.querySelector<HTMLImageElement>('.ws-gallery__lightbox-img');
      const caption = lightbox?.querySelector<HTMLElement>('.ws-gallery__lightbox-caption');
      if (!lightbox || !img) return;

      const src = hit.getAttribute('data-ws-g-src') ?? '';
      const srcset = hit.getAttribute('data-ws-g-srcset') ?? '';
      const alt = hit.getAttribute('data-ws-g-alt') ?? '';
      const captionText = hit.getAttribute('data-ws-g-caption') ?? '';
      const index = hit.getAttribute('data-ws-g-i') ?? '';

      img.src = src;
      if (srcset) img.srcset = srcset;
      else img.removeAttribute('srcset');
      img.alt = alt;
      lightbox.setAttribute('aria-label', alt);
      lightbox.setAttribute('data-ws-g-active', index);

      if (caption) {
        caption.textContent = captionText;
        if (captionText) caption.removeAttribute('hidden');
        else caption.setAttribute('hidden', '');
      }

      const sheetTitle = lightbox.querySelector<HTMLElement>('.ws-gallery__sheet-title');
      if (sheetTitle) {
        const heading = captionText || alt;
        sheetTitle.textContent = heading;
        if (heading) sheetTitle.removeAttribute('hidden');
        else sheetTitle.setAttribute('hidden', '');
      }

      const thumbs = lightbox.querySelectorAll<HTMLElement>('.ws-gallery__sheet-thumb');
      thumbs.forEach((thumb) => {
        if (thumb.getAttribute('data-ws-g-i') === index) {
          thumb.setAttribute('aria-current', 'true');
          // Center the active chip so neighbors before/after stay in view when present.
          thumb.scrollIntoView({
            inline: 'center',
            block: 'nearest',
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      const mark = lightbox.querySelector<HTMLElement>('.ws-gallery__lightbox-index');
      if (mark) {
        const hits = slideHits();
        const pos = hits.findIndex((hit) => hit.getAttribute('data-ws-g-i') === index);
        mark.textContent = String((pos < 0 ? 0 : pos) + 1).padStart(2, '0');
      }
    }

    function slideHits() {
      return [...root!.querySelectorAll<HTMLElement>('.ws-gallery__hit[data-ws-g-src]')].sort(
        (a, b) =>
          Number(a.getAttribute('data-ws-g-i') || 0) - Number(b.getAttribute('data-ws-g-i') || 0),
      );
    }

    function stepLightbox(delta: number) {
      const lightbox = lightboxRoot();
      if (!lightbox) return;
      const hits = slideHits();
      if (hits.length < 2) return;
      const active = lightbox.getAttribute('data-ws-g-active');
      let current = hits.findIndex((hit) => hit.getAttribute('data-ws-g-i') === active);
      if (current < 0) current = 0;
      const next = hits[(current + delta + hits.length) % hits.length];
      if (next) paintLightbox(next);
    }

    function onClick(event: MouseEvent) {
      const hit = (event.target as Element | null)?.closest?.('.ws-gallery__hit[data-ws-g-src]');
      if (hit && root!.contains(hit)) {
        paintLightbox(hit);
        return;
      }

      const thumb = (event.target as Element | null)?.closest?.(
        '.ws-gallery__sheet-thumb[data-ws-g-src]',
      );
      if (thumb && ownsLightboxNode(thumb)) {
        event.preventDefault();
        paintLightbox(thumb);
        return;
      }

      const stepBtn = (event.target as Element | null)?.closest?.(
        '.ws-gallery__nav[data-ws-g-step]',
      );
      if (!(stepBtn instanceof HTMLButtonElement) || !ownsLightboxNode(stepBtn)) return;
      event.preventDefault();
      event.stopPropagation();
      const delta = Number(stepBtn.getAttribute('data-ws-g-step'));
      if (!Number.isFinite(delta) || delta === 0) return;
      stepLightbox(delta);
    }

    const SWIPE_THRESHOLD_PX = 40;
    let swipeStartX: number | null = null;
    let swipeStartY: number | null = null;
    const lightbox = root.querySelector<HTMLElement>('.ws-gallery__lightbox');

    function onPointerDown(event: PointerEvent) {
      if (!lightbox || event.pointerType === 'mouse') return;
      if ((event.target as Element | null)?.closest?.('button')) return;
      swipeStartX = event.clientX;
      swipeStartY = event.clientY;
    }

    function onPointerCancel() {
      swipeStartX = null;
      swipeStartY = null;
    }

    function onPointerUp(event: PointerEvent) {
      if (swipeStartX == null || swipeStartY == null) return;
      const dx = event.clientX - swipeStartX;
      const dy = event.clientY - swipeStartY;
      swipeStartX = null;
      swipeStartY = null;
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
      if (Math.abs(dx) <= Math.abs(dy)) return;
      stepLightbox(dx < 0 ? 1 : -1);
    }

    if (lightbox) {
      lightbox.addEventListener('pointerdown', onPointerDown);
      lightbox.addEventListener('pointercancel', onPointerCancel);
      lightbox.addEventListener('pointerup', onPointerUp);
    }

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function filmstripParts() {
      const scroller = root!.querySelector<HTMLElement>('.ws-gallery__scroller');
      const dotsNav = root!.querySelector<HTMLElement>('.ws-gallery__dots');
      if (!scroller || !dotsNav) return null;
      const frames = [...scroller.querySelectorAll<HTMLElement>('.ws-gallery__frame')];
      const dots = [...dotsNav.querySelectorAll<HTMLButtonElement>('.ws-gallery__dot')];
      if (frames.length < 2 || dots.length < 2) return null;
      return { scroller, frames, dots, dotsNav };
    }

    function setFilmstripActive(dots: HTMLButtonElement[], index: number) {
      dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function nearestFilmstripIndex(scroller: HTMLElement, frames: HTMLElement[]) {
      const origin = scroller.getBoundingClientRect().left;
      let best = 0;
      let bestDist = Infinity;
      frames.forEach((frame, i) => {
        const dist = Math.abs(frame.getBoundingClientRect().left - origin);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function scrollFilmstripTo(
      parts: {
        scroller: HTMLElement;
        frames: HTMLElement[];
        dots: HTMLButtonElement[];
      },
      index: number,
    ) {
      const frame = parts.frames[index];
      if (!frame) return;
      frame.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        inline: 'start',
        block: 'nearest',
      });
      setFilmstripActive(parts.dots, index);
    }

    let detachFilmstrip: (() => void) | undefined;
    if (arrangement === 'filmstrip') {
      const parts = filmstripParts();
      if (parts) {
        let ticking = false;
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            setFilmstripActive(
              parts.dots,
              nearestFilmstripIndex(parts.scroller, parts.frames),
            );
          });
        };
        const onDotClick = (event: MouseEvent) => {
          const dot = (event.target as Element | null)?.closest?.('.ws-gallery__dot');
          if (!(dot instanceof HTMLButtonElement) || !parts.dotsNav.contains(dot)) return;
          const index = parts.dots.indexOf(dot);
          if (index < 0) return;
          scrollFilmstripTo(parts, index);
        };
        parts.scroller.addEventListener('scroll', onScroll, { passive: true });
        parts.dotsNav.addEventListener('click', onDotClick);
        setFilmstripActive(parts.dots, nearestFilmstripIndex(parts.scroller, parts.frames));
        detachFilmstrip = () => {
          parts.scroller.removeEventListener('scroll', onScroll);
          parts.dotsNav.removeEventListener('click', onDotClick);
        };
      }
    }

    function onKey(event: KeyboardEvent) {
      const open = root!.querySelector<HTMLElement>('.ws-gallery__lightbox:popover-open');
      const focusInside = root!.contains(document.activeElement);
      if (!open && !focusInside) return;

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const delta = event.key === 'ArrowRight' ? 1 : -1;

      if (open) {
        stepLightbox(delta);
        event.preventDefault();
        return;
      }

      if (arrangement === 'spotlight' && focusInside) {
        const spots = [...root!.querySelectorAll<HTMLInputElement>('.ws-gallery__spot')];
        const current = spots.findIndex((radio) => radio.checked);
        if (current < 0 || spots.length === 0) return;
        const next = spots[(current + delta + spots.length) % spots.length];
        if (next) {
          next.checked = true;
          next.focus();
          event.preventDefault();
        }
        return;
      }

      if (arrangement === 'filmstrip' && focusInside) {
        const parts = filmstripParts();
        if (!parts) return;
        const current = nearestFilmstripIndex(parts.scroller, parts.frames);
        const next = (current + delta + parts.frames.length) % parts.frames.length;
        scrollFilmstripTo(parts, next);
        event.preventDefault();
      }
    }

    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
      if (lightbox) {
        lightbox.removeEventListener('pointerdown', onPointerDown);
        lightbox.removeEventListener('pointercancel', onPointerCancel);
        lightbox.removeEventListener('pointerup', onPointerUp);
      }
      detachFilmstrip?.();
    };
  }, [arrangement, editable, filledKey, lightboxLook]);

  return (
    <section
      ref={rootRef}
      className={[
        'ws-gallery',
        galleryArrangementClass(arrangement),
        editable ? 'ws-gallery--editing' : '',
        isActive ? 'ws-block--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-lightbox={lightboxLook}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      data-arrangement={arrangement}
      id="gallery"
      aria-labelledby={sectionTitle ? titleId : undefined}
    >
      <div className="ws-gallery__inner">
        <header className="ws-gallery__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-gallery__title"
            id={titleId}
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            as="p"
            fieldKey="sectionSubtitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-gallery__subtitle"
          >
            {sectionSubtitle}
          </EditableContent>
        </header>

        {arrangement === 'spotlight' && !editable && filled.length > 0 ? (
          <div className="ws-gallery__stage">
            {filled.map((slot) => {
              const alt = slotAlt(slot, sectionTitle);
              const src = frameSrc(slot.rawUrl, 1280);
              const srcSet = frameSrcSet(slot.rawUrl);
              if (!src) return null;
              return (
                <div
                  key={`stage-${slot.index}`}
                  className="ws-gallery__stage-shot"
                  data-index={slot.index}
                >
                  <button
                    type="button"
                    className="ws-gallery__hit"
                    popoverTarget={`${groupId}-lb`}
                    data-ws-g-src={src}
                    data-ws-g-srcset={srcSet ?? ''}
                    data-ws-g-alt={alt}
                    data-ws-g-caption={slot.caption ?? ''}
                    data-ws-g-i={String(slot.index)}
                    aria-label={alt}
                  >
                    <span className="ws-gallery__media">
                      <img
                        className="ws-gallery__img"
                        src={src}
                        srcSet={srcSet}
                        sizes={SITE_IMAGE_SIZES.gallerySpotlight}
                        alt={alt}
                        width={1280}
                        height={960}
                        decoding="async"
                      />
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        <div
          className={
            arrangement === 'filmstrip' ? 'ws-gallery__scroller' : 'ws-gallery__board'
          }
        >
          <ul className="ws-gallery__frames">
            {slots.map((slot, position) => {
              const src = frameSrc(slot.rawUrl, arrangement === 'spotlight' ? 480 : 960);
              const srcSet = frameSrcSet(slot.rawUrl);
              const alt = slotAlt(slot, sectionTitle);
              const sizes =
                arrangement === 'filmstrip'
                  ? SITE_IMAGE_SIZES.galleryFilmstrip
                  : arrangement === 'ledger'
                    ? SITE_IMAGE_SIZES.galleryLedger
                    : arrangement === 'spotlight'
                      ? SITE_IMAGE_SIZES.galleryThumb
                      : arrangement === 'masonry'
                        ? SITE_IMAGE_SIZES.galleryMasonry
                        : position === 0
                          ? SITE_IMAGE_SIZES.galleryMosaicLead
                          : SITE_IMAGE_SIZES.galleryMosaicCell;

              return (
                <li
                  key={slot.mediaKey}
                  className="ws-gallery__frame"
                  style={{ ['--ws-gallery-i' as string]: String(position) }}
                  data-index={slot.index}
                >
                  {arrangement === 'spotlight' && !editable && src ? (
                    <>
                      <input
                        className="ws-gallery__spot ws-gallery__control"
                        type="radio"
                        name={`${groupId}-spot`}
                        id={`${groupId}-spot-${slot.index}`}
                        data-index={slot.index}
                        defaultChecked={position === 0}
                      />
                      <label className="ws-gallery__thumb" htmlFor={`${groupId}-spot-${slot.index}`}>
                        <span className="ws-gallery__media">
                          <img
                            src={src}
                            srcSet={srcSet}
                            sizes={sizes}
                            alt=""
                            width={240}
                            height={180}
                            loading="lazy"
                            decoding="async"
                          />
                        </span>
                        <span className="ws-gallery__index" aria-hidden="true">
                          {padIndex(slot.index)}
                        </span>
                      </label>
                    </>
                  ) : (
                    <FrameMedia
                      slot={slot}
                      editable={editable}
                      activeEditTarget={target}
                      src={src}
                      srcSet={srcSet}
                      lightboxSrc={frameSrc(slot.rawUrl, 1280)}
                      lightboxSrcSet={frameSrcSet(slot.rawUrl)}
                      sizes={sizes}
                      alt={alt}
                      groupId={groupId}
                      showIndex={arrangement === 'ledger' || arrangement === 'filmstrip'}
                    />
                  )}
                  {arrangement === 'spotlight' && !editable ? (
                    slot.caption ? (
                      <p className="ws-gallery__caption">{slot.caption}</p>
                    ) : null
                  ) : arrangement === 'masonry' && !editable ? (
                    slot.caption ? (
                      <p className="ws-gallery__caption">{slot.caption}</p>
                    ) : null
                  ) : (
                    <EditableContent
                      as="p"
                      fieldKey={slot.captionKey}
                      editable={editable}
                      activeEditTarget={target}
                      className="ws-gallery__caption"
                    >
                      {slot.caption}
                    </EditableContent>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {arrangement === 'filmstrip' && !editable && filled.length > 1 ? (
          <nav className="ws-gallery__dots" aria-label={filmstripNavLabel}>
            {filled.map((slot, position) => (
              <button
                key={`dot-${slot.mediaKey}`}
                type="button"
                className="ws-gallery__dot"
                data-index={slot.index}
                aria-label={goToPhotoLabel.replace('{n}', String(slot.index))}
                aria-current={position === 0 ? 'true' : undefined}
              />
            ))}
          </nav>
        ) : null}

        {!editable && filled.length > 0 ? (
          <GalleryViewer
            slots={filled}
            groupId={groupId}
            sectionTitle={sectionTitle}
            look={lightboxLook}
            closeLabel={closeLabel}
            prevLabel={prevLabel}
            nextLabel={nextLabel}
            goToPhotoLabel={goToPhotoLabel}
          />
        ) : null}
      </div>
    </section>
  );
}

function FrameMedia({
  slot,
  editable,
  activeEditTarget,
  src,
  srcSet,
  lightboxSrc,
  lightboxSrcSet,
  sizes,
  alt,
  groupId,
  showIndex,
}: {
  slot: GalleryFrameSlot;
  editable?: boolean;
  activeEditTarget: BlockRenderProps['activeEditTarget'];
  src?: string;
  srcSet?: string;
  lightboxSrc?: string;
  lightboxSrcSet?: string;
  sizes: string;
  alt: string;
  groupId: string;
  showIndex: boolean;
}) {
  const media = src ? (
    <EditableMedia
      fieldKey={slot.mediaKey}
      editable={editable}
      activeEditTarget={activeEditTarget}
      className="ws-gallery__img"
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      width={960}
      height={720}
      loading="lazy"
      decoding="async"
    />
  ) : (
    <div className="ws-gallery__placeholder" aria-hidden="true" />
  );

  return (
    <>
      {showIndex ? (
        <span className="ws-gallery__index" aria-hidden="true">
          {padIndex(slot.index)}
        </span>
      ) : null}
      {src && lightboxSrc && !editable ? (
        <button
          type="button"
          className="ws-gallery__hit"
          popoverTarget={`${groupId}-lb`}
          data-ws-g-src={lightboxSrc}
          data-ws-g-srcset={lightboxSrcSet ?? ''}
          data-ws-g-alt={alt}
          data-ws-g-caption={slot.caption ?? ''}
          data-ws-g-i={String(slot.index)}
          aria-label={alt}
        >
          <span className="ws-gallery__media">{media}</span>
        </button>
      ) : (
        <div className="ws-gallery__media">{media}</div>
      )}
    </>
  );
}

function LightboxClose({ popoverId, label }: { popoverId: string; label: string }) {
  return (
    <button
      type="button"
      className="ws-gallery__close"
      popoverTarget={popoverId}
      popoverTargetAction="hide"
      aria-label={label}
    >
      <LucideIcon icon={X} size="md" />
    </button>
  );
}

function LightboxNav({
  prevLabel,
  nextLabel,
  iconSize = 'md',
}: {
  prevLabel: string;
  nextLabel: string;
  iconSize?: 'sm' | 'md' | 'lg';
}) {
  return (
    <>
      <button
        type="button"
        className="ws-gallery__nav ws-gallery__nav--prev"
        data-ws-g-step="-1"
        aria-label={prevLabel}
      >
        <LucideIcon icon={ChevronLeft} size={iconSize} />
      </button>
      <button
        type="button"
        className="ws-gallery__nav ws-gallery__nav--next"
        data-ws-g-step="1"
        aria-label={nextLabel}
      >
        <LucideIcon icon={ChevronRight} size={iconSize} />
      </button>
    </>
  );
}

function LightboxPhoto({ src, srcSet, alt }: { src: string; srcSet?: string; alt: string }) {
  return (
    <img
      className="ws-gallery__lightbox-img"
      src={src}
      srcSet={srcSet}
      sizes={SITE_IMAGE_SIZES.galleryLightbox}
      alt={alt}
      width={1280}
      height={960}
      decoding="async"
    />
  );
}

function GalleryViewer({
  slots,
  groupId,
  sectionTitle,
  look,
  closeLabel,
  prevLabel,
  nextLabel,
  goToPhotoLabel,
}: {
  slots: GalleryFrameSlot[];
  groupId: string;
  sectionTitle: string;
  look: GalleryLightbox;
  closeLabel: string;
  prevLabel: string;
  nextLabel: string;
  goToPhotoLabel: string;
}) {
  const showNav = slots.length > 1;
  const first = slots[0];
  if (!first) return null;
  const alt = slotAlt(first, sectionTitle);
  const photoTitle = first.caption || alt;
  const src = frameSrc(first.rawUrl, 1280);
  const srcSet = frameSrcSet(first.rawUrl);
  if (!src) return null;
  const popoverId = `${groupId}-lb`;
  const countLabel = padIndex(1);
  const totalLabel = padIndex(slots.length);

  return (
    <div className="ws-gallery__viewer">
      <div
        id={popoverId}
        className={['ws-gallery__lightbox', galleryLightboxClass(look)]
          .filter(Boolean)
          .join(' ')}
        data-lightbox={look}
        data-ws-g-active={String(first.index)}
        popover="auto"
        role="dialog"
        aria-label={alt}
      >
        {look === 'catalog' ? (
          <div className="ws-gallery__dialog">
            <header className="ws-gallery__sheet-head">
              <p className="ws-gallery__sheet-title" hidden={!photoTitle}>
                {photoTitle}
              </p>
              <LightboxClose popoverId={popoverId} label={closeLabel} />
            </header>
            <div className="ws-gallery__sheet-stage">
              <div className="ws-gallery__sheet-photo">
                {showNav ? <LightboxNav prevLabel={prevLabel} nextLabel={nextLabel} /> : null}
                <LightboxPhoto src={src} srcSet={srcSet} alt={alt} />
              </div>
            </div>
            {showNav ? (
              <ul className="ws-gallery__sheet-thumbs">
                {slots.map((slot) => {
                  const thumbSrc = frameSrc(slot.rawUrl, 240);
                  const fullSrc = frameSrc(slot.rawUrl, 1280);
                  if (!thumbSrc || !fullSrc) return null;
                  const thumbAlt = slotAlt(slot, sectionTitle);
                  return (
                    <li key={slot.mediaKey}>
                      <button
                        type="button"
                        className="ws-gallery__sheet-thumb"
                        data-ws-g-src={fullSrc}
                        data-ws-g-srcset={frameSrcSet(slot.rawUrl) ?? ''}
                        data-ws-g-alt={thumbAlt}
                        data-ws-g-caption={slot.caption ?? ''}
                        data-ws-g-i={String(slot.index)}
                        aria-current={slot.index === first.index ? 'true' : undefined}
                        aria-label={goToPhotoLabel.replace('{n}', String(slot.index))}
                      >
                        <img src={thumbSrc} alt="" width={240} height={180} decoding="async" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : look === 'cinema' ? (
          <div className="ws-gallery__dialog">
            <LightboxClose popoverId={popoverId} label={closeLabel} />
            {showNav ? (
              <LightboxNav prevLabel={prevLabel} nextLabel={nextLabel} iconSize="lg" />
            ) : null}
            <LightboxPhoto src={src} srcSet={srcSet} alt={alt} />
            <div className="ws-gallery__lightbox-meta">
              <p className="ws-gallery__lightbox-count">
                <span className="ws-gallery__lightbox-index">{countLabel}</span>
                <span className="ws-gallery__lightbox-total">{` / ${totalLabel}`}</span>
              </p>
              <p className="ws-gallery__lightbox-caption" hidden={!first.caption}>
                {first.caption}
              </p>
            </div>
          </div>
        ) : (
          <div className="ws-gallery__dialog">
            <LightboxClose popoverId={popoverId} label={closeLabel} />
            {showNav ? <LightboxNav prevLabel={prevLabel} nextLabel={nextLabel} /> : null}
            <LightboxPhoto src={src} srcSet={srcSet} alt={alt} />
            <p className="ws-gallery__lightbox-caption" hidden={!first.caption}>
              {first.caption}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
